import React from 'react';
import { Chip, styled } from '@mui/material';
import { EventBusyOutlined, EventAvailableOutlined } from '@mui/icons-material';
import { format, isValid } from 'date-fns';
import { moneyFormat } from 'helpers/moneyFormat';
import { PatientRecord } from 'types/patientRecord';
import { plural, RecordMetric, RecordMetrics } from 'components/record-ficha/RecordMetric';

const Strip = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flexWrap: 'wrap',
}));

function formatDate(value: string | null) {
  if (!value) return null;

  const date = new Date(value.replace(' ', 'T'));
  return isValid(date) ? format(date, 'dd/MM/yyyy') : null;
}

interface PatientRecordSummaryProps {
  record: PatientRecord;
}

// Ticket médio das guias (concluídas) e dos orçamentos (fechados) ficam separados, nunca somados.
const PatientRecordSummary: React.FC<PatientRecordSummaryProps> = ({ record }) => {
  const { guides, budgets } = record;
  const completed = guides.summary.completed;
  const closed = budgets.summary.closed;
  const open = budgets.summary.by_status?.open;
  const openCount = open?.count ?? 0;
  const noShows = guides.summary.by_status?.no_show?.count ?? 0;
  const lastAppointment = formatDate(guides.summary.last_appointment_date);

  return (
    <>
      <Strip>
        <Chip
          size="small"
          variant="outlined"
          icon={<EventBusyOutlined />}
          color={noShows > 0 ? 'warning' : 'default'}
          label={plural(noShows, 'falta', 'faltas')}
        />
        <Chip
          size="small"
          variant="outlined"
          icon={<EventAvailableOutlined />}
          label={`Última consulta: ${lastAppointment || '-'}`}
        />
      </Strip>

      <RecordMetrics>
        <RecordMetric
          label="Orçamentos fechados"
          value={moneyFormat(closed.value_total)}
          helpers={[plural(closed.count, 'fechado', 'fechados'), `Ticket médio ${moneyFormat(closed.average_ticket)}`]}
        />
        <RecordMetric
          label="Guias concluídas"
          value={moneyFormat(completed.value_total)}
          helpers={[
            plural(completed.count, 'concluída', 'concluídas'),
            `Ticket médio ${moneyFormat(completed.average_ticket)}`,
          ]}
        />
        <RecordMetric
          label="Orçamentos em aberto"
          value={moneyFormat(open?.value_total ?? 0)}
          helpers={[plural(openCount, 'orçamento', 'orçamentos')]}
        />
      </RecordMetrics>
    </>
  );
};

export default PatientRecordSummary;
